import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Users, Search, Settings, Plus, Edit3, Trash2, Save, X, Loader2, CheckCircle } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '@/data/store/utils/redux';
import { getAllRolesAsync, createRoleAsync, updateRoleAsync, deleteRoleAsync } from '@/data/store/actions';
import { getAllRoles } from '@/data/store/selectors';
import { TRoleItem, TCreateRolePayload, TUpdateRolePayload } from '@/data/types';
import { InfiniteScroll } from '@/components/common/InfiniteScroll/InfiniteScroll';
import { generateRoleId } from '@/utils/roles';
import ConfirmationModal from '@/components/ui/ConfirmationModal';
import RoleBackground from './RoleBackground';

interface RoleSelectionProps {
  selectedRole: TRoleItem | null;
  onRoleSelect: (role: TRoleItem | null) => void;
  hasSelectedRole: boolean;
  onRoleEditingStateChange?: (isEditing: boolean) => void;
  selectedPermissions?: string[];
}

interface DeleteConfirmationState {
  isOpen: boolean;
  role: TRoleItem | null;
  isDeleting: boolean;
}

const RoleSelection: React.FC<RoleSelectionProps> = ({
  selectedRole,
  onRoleSelect,
  hasSelectedRole,
  onRoleEditingStateChange,
  selectedPermissions = [],
}) => {
  const dispatch = useAppDispatch();
  const rolesData = useSelector(getAllRoles);

  const [isCreatingRole, setIsCreatingRole] = useState(false);
  const [editingRole, setEditingRole] = useState<TRoleItem | null>(null);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDescription, setNewRoleDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rolesScrollIndex, setRolesScrollIndex] = useState(0);

  // Delete confirmation state
  const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmationState>({
    isOpen: false,
    role: null,
    isDeleting: false,
  });

  // API call function for roles
  const attemptGetAllRoles = useCallback(
    async (params: any) => {
      return await dispatch(getAllRolesAsync(params));
    },
    [dispatch],
  );

  const handleCreateRole = async () => {
    if (!newRoleName.trim()) return;

    setIsSubmitting(true);
    try {
      const payload: TCreateRolePayload = {
        id: generateRoleId(newRoleName),
        name: newRoleName.trim(),
        description: newRoleDescription.trim() || undefined,
        weight: 1,
        permission_ids: selectedPermissions,
      };

      const response = await dispatch(createRoleAsync({ payload }));

      // Refresh the roles list
      setRolesScrollIndex((prev) => prev + 1);

      // Close form and reset
      setIsCreatingRole(false);
      setEditingRole(null);
      setNewRoleName('');
      setNewRoleDescription('');
    } catch (error) {
      console.error('Failed to create role:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!editingRole || !newRoleName.trim()) return;

    setIsSubmitting(true);
    try {
      const payload: Partial<TUpdateRolePayload> = {
        name: newRoleName.trim(),
        description: newRoleDescription.trim() || undefined,
        weight: editingRole.weight || 1,
        permission_ids: selectedPermissions,
      };

      await dispatch(updateRoleAsync({ roleId: editingRole.id, payload }));
      setRolesScrollIndex((prev) => prev + 1);

      // Close form and reset
      setEditingRole(null);
      setIsCreatingRole(false);
      setNewRoleName('');
      setNewRoleDescription('');
      // Notify parent that we're exiting edit mode
      onRoleEditingStateChange?.(false);
    } catch (error) {
      console.error('Failed to update role:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditRole = (role: TRoleItem) => {
    setEditingRole(role);
    setNewRoleName(role.name);
    setNewRoleDescription(role.description || '');
    setIsCreatingRole(true);
    // Notify parent that we're entering edit mode
    onRoleEditingStateChange?.(true);
  };

  const handleDeleteRole = async (role: TRoleItem) => {
    setDeleteConfirmation({
      isOpen: true,
      role,
      isDeleting: false,
    });
  };

  const confirmDeleteRole = async () => {
    if (!deleteConfirmation.role) return;

    setDeleteConfirmation((prev) => ({ ...prev, isDeleting: true }));

    try {
      await dispatch(deleteRoleAsync({ roleId: deleteConfirmation.role.id }));
      setRolesScrollIndex((prev) => prev + 1);

      // If we deleted the currently selected role, clear selection
      if (selectedRole?.id === deleteConfirmation.role.id) {
        onRoleSelect(null);
      }

      // Close the modal
      setDeleteConfirmation({
        isOpen: false,
        role: null,
        isDeleting: false,
      });
    } catch (error) {
      console.error('Failed to delete role:', error);
      setDeleteConfirmation((prev) => ({ ...prev, isDeleting: false }));
    }
  };

  const cancelDeleteRole = () => {
    setDeleteConfirmation({
      isOpen: false,
      role: null,
      isDeleting: false,
    });
  };

  const closeRoleForm = () => {
    setIsCreatingRole(false);
    setEditingRole(null);
    setNewRoleName('');
    setNewRoleDescription('');
    // Notify parent that we're exiting edit mode
    onRoleEditingStateChange?.(false);
  };

  const handleCustomRoleSelect = () => {
    // Clear any role selection to allow custom permissions
    onRoleSelect(null);
  };

  // Handle role selection/deselection
  const handleRoleClick = (role: TRoleItem) => {
    if (selectedRole?.id === role.id) {
      // If clicking the already selected role, deselect it
      onRoleSelect(null);
    } else {
      // Select the new role
      onRoleSelect(role);
    }
  };

  // Role Card Component
  const RoleCard: React.FC<{
    role: TRoleItem;
    isSelected: boolean;
  }> = ({ role, isSelected }) => (
    <motion.div
      layout
      whileHover={{ scale: 1.03, y: -4 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => handleRoleClick(role)}
      className={`group relative cursor-pointer rounded-2xl border-2 p-6 transition-all duration-300 ${
        isSelected
          ? 'border-blue-400 bg-gradient-to-br from-blue-50 to-indigo-50 shadow-xl shadow-blue-200/30'
          : 'border-gray-200 bg-gradient-to-br from-gray-50 to-white hover:border-blue-300 hover:shadow-lg'
      }`}
    >
      {/* Action buttons */}
      <div className="absolute top-3 right-3 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleEditRole(role);
          }}
          className="rounded p-1.5 text-gray-400 transition-colors hover:bg-blue-100 hover:text-blue-600"
        >
          <Edit3 className="h-3 w-3" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteRole(role);
          }}
          className="rounded p-1.5 text-gray-400 transition-colors hover:bg-red-100 hover:text-red-600"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>

      <div className="flex flex-col items-center text-center">
        <div
          className={`mb-4 rounded-xl p-4 transition-all duration-300 ${
            isSelected
              ? 'bg-blue-100 text-blue-700'
              : 'bg-gray-100 text-gray-500 group-hover:bg-blue-100 group-hover:text-blue-600'
          }`}
        >
          <Shield className="h-6 w-6" />
        </div>

        <h3
          className={`text-lg font-bold transition-colors duration-300 ${
            isSelected ? 'text-blue-900' : 'text-gray-700 group-hover:text-blue-800'
          }`}
        >
          {role.name}
        </h3>

        <p
          className={`mt-2 text-sm leading-relaxed transition-colors duration-300 ${
            isSelected ? 'text-blue-700' : 'text-gray-500 group-hover:text-blue-600'
          }`}
        >
          {role.description || 'Προσαρμοσμένος ρόλος'}
        </p>

        <div className="mt-4 flex items-center gap-4 text-xs text-gray-600">
          <span>{role.users || 0} χρήστες</span>
          <span>{role.permissions || 0} δικαιώματα</span>
        </div>

        {isSelected && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-blue-500"
          >
            <CheckCircle className="h-4 w-4 text-white" />
          </motion.div>
        )}
      </div>
    </motion.div>
  );

  // Custom Role Card Component
  const CustomRoleCard: React.FC<{
    isSelected: boolean;
  }> = ({ isSelected }) => (
    <motion.div
      layout
      whileHover={{ scale: 1.03, y: -4 }}
      whileTap={{ scale: 0.98 }}
      onClick={handleCustomRoleSelect}
      className={`group cursor-pointer rounded-2xl border-2 border-dashed p-6 transition-all duration-300 ${
        isSelected
          ? 'border-purple-400 bg-gradient-to-br from-purple-50 to-pink-50 shadow-xl shadow-purple-200/30'
          : 'border-gray-300 bg-gradient-to-br from-gray-50 to-white hover:border-purple-300 hover:shadow-lg'
      }`}
    >
      <div className="flex flex-col items-center justify-center text-center">
        <div
          className={`mb-4 rounded-xl p-4 transition-all duration-300 ${
            isSelected
              ? 'bg-purple-100 text-purple-700'
              : 'bg-gray-100 text-gray-500 group-hover:bg-purple-100 group-hover:text-purple-600'
          }`}
        >
          {isSelected ? <Settings className="h-6 w-6" /> : <Plus className="h-6 w-6" />}
        </div>

        <h3
          className={`text-lg font-bold transition-colors duration-300 ${
            isSelected ? 'text-purple-900' : 'text-gray-700 group-hover:text-purple-800'
          }`}
        >
          Προσαρμοσμένος Ρόλος
        </h3>

        <p
          className={`mt-2 text-sm leading-relaxed transition-colors duration-300 ${
            isSelected ? 'text-purple-700' : 'text-gray-500 group-hover:text-purple-600'
          }`}
        >
          Δημιουργήστε έναν ρόλο με προσαρμοσμένα δικαιώματα που ταιριάζουν στις ανάγκες σας
        </p>

        <div className="mt-4">
          <span
            className={`inline-block rounded-full px-3 py-1 text-xs font-medium transition-colors duration-300 ${
              isSelected
                ? 'bg-purple-200 text-purple-800'
                : 'bg-gray-200 text-gray-600 group-hover:bg-purple-200 group-hover:text-purple-700'
            }`}
          >
            Πλήρης έλεγχος δικαιωμάτων
          </span>
        </div>

        {isSelected && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-purple-500"
          >
            <CheckCircle className="h-4 w-4 text-white" />
          </motion.div>
        )}
      </div>
    </motion.div>
  );

  // Render roles function for InfiniteScroll
  const renderRoles = useCallback(
    (roles: TRoleItem[], state: any) => {
      if (roles.length === 0 && !state.loading) {
        return (
          <div className="py-8 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
              <Users className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="mb-2 text-lg font-medium text-gray-900">Δεν βρέθηκαν ρόλοι</h3>
            <p className="mb-4 text-sm text-gray-600">Δημιουργήστε τον πρώτο σας ρόλο για να ξεκινήσετε</p>
          </div>
        );
      }

      return (
        <motion.div className="space-y-4">
          {roles.map((role) => (
            <RoleCard key={role.id} role={role} isSelected={selectedRole?.id === role.id} />
          ))}

          {/* Custom role option */}
          <CustomRoleCard isSelected={selectedRole === null && hasSelectedRole} />
        </motion.div>
      );
    },
    [selectedRole, hasSelectedRole],
  );

  const renderLoading = useCallback((isInitial: boolean) => {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="flex flex-col items-center">
          <Loader2 className="mb-2 h-6 w-6 animate-spin text-blue-600" />
          <p className="text-sm text-gray-600">Φόρτωση ρόλων...</p>
        </div>
      </div>
    );
  }, []);

  const renderError = useCallback((error: string, retry: () => void) => {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
          <X className="h-6 w-6 text-red-500" />
        </div>
        <h3 className="mb-2 text-lg font-medium text-gray-900">Σφάλμα φόρτωσης ρόλων</h3>
        <p className="mb-4 text-center text-sm text-gray-500">{error}</p>
        <button
          onClick={retry}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white transition-colors hover:bg-blue-700"
        >
          Δοκιμάστε ξανά
        </button>
      </div>
    );
  }, []);

  return (
    <motion.div
      initial={false}
      animate={{
        width: hasSelectedRole ? '384px' : '100%',
      }}
      transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
      className="sticky top-6 flex-shrink-0 self-start transition-all duration-700 ease-out"
    >
      <motion.div
        initial={false}
        animate={{
          borderRadius: '1rem',
        }}
        transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
        className={`relative h-[calc(100vh-1.5rem)] min-h-[600px] border border-gray-200 shadow-xl transition-all duration-700 ${
          hasSelectedRole ? 'mr-6' : ''
        }`}
      >
        {/* Background */}
        <div className="absolute inset-0">
          <RoleBackground />
        </div>

        {/* Content */}
        <div className="relative z-10 flex h-full flex-col p-6">
          {/* Create Role Button */}
          <div className="mb-6 flex-shrink-0">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setIsCreatingRole(true)}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 font-medium text-white transition-colors hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" />
              Δημιουργία Νέου Ρόλου
            </motion.button>
          </div>

          {/* Role Creation Form */}
          <AnimatePresence>
            {isCreatingRole && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-6 flex-shrink-0 rounded-xl border bg-white/90 p-4 backdrop-blur"
              >
                <div className="mb-4 flex items-center justify-between">
                  <h4 className="font-semibold text-gray-900">{editingRole ? 'Επεξεργασία Ρόλου' : 'Νέος Ρόλος'}</h4>
                  <button onClick={closeRoleForm} className="rounded p-1 text-gray-400 hover:text-gray-600">
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="Όνομα ρόλου"
                    value={newRoleName}
                    onChange={(e) => setNewRoleName(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                  />
                  <textarea
                    placeholder="Περιγραφή (προαιρετικό)"
                    value={newRoleDescription}
                    onChange={(e) => setNewRoleDescription(e.target.value)}
                    rows={2}
                    className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={closeRoleForm}
                      disabled={isSubmitting}
                      className="flex-1 rounded-lg bg-gray-100 px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-200"
                    >
                      Ακύρωση
                    </button>
                    <button
                      onClick={editingRole ? handleUpdateRole : handleCreateRole}
                      disabled={!newRoleName.trim() || isSubmitting}
                      className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm text-white transition-colors hover:bg-blue-700 disabled:bg-gray-400"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Αποθήκευση...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4" />
                          {editingRole ? 'Ενημέρωση' : 'Δημιουργία'}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Scrollable Roles List Container */}
          <div className="flex-1 overflow-hidden">
            <div className="h-full overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent hover:scrollbar-thumb-gray-400">
              <InfiniteScroll
                apiAction={attemptGetAllRoles}
                itemsData={rolesData}
                initialPageSize={20}
                renderLoading={renderLoading}
                renderError={renderError}
                scroll={{
                  threshold: 200,
                  debounceMs: 100,
                }}
                key={`roles-${rolesScrollIndex}`}
              >
                {renderRoles}
              </InfiniteScroll>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteConfirmation.isOpen}
        onClose={cancelDeleteRole}
        onConfirm={confirmDeleteRole}
        title="Επιβεβαίωση Διαγραφής"
        message={`Είστε σίγουροι ότι θέλετε να διαγράψετε τον ρόλο "${deleteConfirmation.role?.name}"; Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.`}
        confirmText="Διαγραφή Ρόλου"
        cancelText="Ακύρωση"
        variant="danger"
        isLoading={deleteConfirmation.isDeleting}
      />
    </motion.div>
  );
};

export default RoleSelection;
